import React from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, ArrowUpRight, MicrophoneStage, Waveform, DownloadSimple } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";

export default function About() {
  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white flex flex-col" data-testid="about-page">
      {/* Header — mirrors Contact's navigation */}
      <header className="border-b border-white/10">
        <div className="max-w-[1500px] mx-auto px-6 lg:px-12 py-6 flex items-center justify-between">
          <Link
            to="/"
            data-testid="about-back-home"
            className="inline-flex items-center gap-2 font-mono uppercase text-xs tracking-widest text-white/60 hover:text-[#A8FF1F] transition-colors"
          >
            <ArrowLeft size={14} weight="bold" /> BACK
          </Link>
          <Link to="/" className="font-display text-2xl tracking-tighter">BEAT BOX</Link>
          <span className="font-mono uppercase text-xs tracking-widest text-white/40 hidden md:inline">ABOUT US</span>
        </div>
      </header>

      <main className="flex-1">
        <section className="max-w-[1500px] mx-auto px-6 lg:px-12 py-16 lg:py-24">
          {/* Editorial page title */}
          <div className="mb-14 lg:mb-20 max-w-3xl">
            <span className="inline-block border border-[#A8FF1F]/40 bg-[#A8FF1F]/10 text-[#A8FF1F] font-mono uppercase text-xs tracking-widest rounded-full px-3 py-1">
              ABOUT BEAT BOX
            </span>
            <h1 className="font-display text-5xl md:text-6xl lg:text-7xl tracking-tighter leading-[0.95] mt-6">
              Born from hip-hop.<br />Rebuilt for the future.
            </h1>
          </div>

          {/* Story — editorial two-column rhythm */}
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-6">
            <div className="lg:col-span-7 space-y-6 text-white/75 text-base lg:text-lg leading-relaxed">
              <p>
                Beatboxing was born from the creativity of hip-hop culture&mdash;when artists transformed the
                human voice into drums, basslines, sound effects, and rhythm. Pioneers such as Doug E. Fresh,
                Biz Markie, and generations of human beatboxers carried that unmistakable sound from
                neighborhood gatherings and cyphers to stages around the world.
              </p>
              <p>
                Music producers later used pocket-sized cassette recorders&mdash;and eventually phone voice
                memos&mdash;to capture melodies and rhythms before inspiration disappeared. But recording the
                idea was only the beginning. Turning that rough vocal concept into a finished track still
                required equipment, production software, and hours inside a studio.
              </p>
              <p className="font-display text-2xl md:text-3xl tracking-tighter text-white">
                Beat Box changes that.
              </p>
              <p>
                Beatbox a rhythm. Hum a melody. Sing a hook. Beat Box captures your idea and helps transform
                each vocal sound into its own instrument inside a full browser-based music studio.
              </p>
              <p>
                Sync and quantize from 1/4 to 1/32. Build multiple layers. Arrange your track. Get guidance
                from your 24/7 AI studio assistant. Then export your mix to the mastering platform you already
                use&mdash;without ever stepping inside a traditional recording studio.
              </p>
              <p>
                Want something fast? Create it, download it, and go. Your beats can sound good straight out of
                the gate.
              </p>
            </div>

            {/* Side rail — feature accents in brand cards */}
            <div className="lg:col-span-4 lg:col-start-9 space-y-4">
              <div className="bb-card p-6 bg-[#0F1013] border-white/10">
                <div className="w-11 h-11 rounded-full bg-[#A8FF1F] flex items-center justify-center mb-4">
                  <MicrophoneStage size={20} weight="fill" className="text-[#0A0A0A]" />
                </div>
                <p className="font-mono uppercase text-xs tracking-widest text-white/50 mb-1.5">01 · CAPTURE</p>
                <p className="text-white/75 text-sm leading-relaxed">Beatbox, hum or sing straight into the browser — no gear, no setup.</p>
              </div>
              <div className="bb-card p-6 bg-[#A8FF1F] text-[#0A0A0A] border-[#A8FF1F]">
                <div className="w-11 h-11 rounded-full bg-[#0A0A0A] flex items-center justify-center mb-4">
                  <Waveform size={20} weight="fill" className="text-[#A8FF1F]" />
                </div>
                <p className="font-mono uppercase text-xs tracking-widest text-[#0A0A0A]/60 mb-1.5">02 · TRANSFORM</p>
                <p className="text-[#0A0A0A]/80 text-sm leading-relaxed">Every vocal sound becomes drums, instruments and layered arrangements.</p>
              </div>
              <div className="bb-card p-6 bg-[#0F1013] border-white/10">
                <div className="w-11 h-11 rounded-full bg-[#A8FF1F] flex items-center justify-center mb-4">
                  <DownloadSimple size={20} weight="fill" className="text-[#0A0A0A]" />
                </div>
                <p className="font-mono uppercase text-xs tracking-widest text-white/50 mb-1.5">03 · MASTER</p>
                <p className="text-white/75 text-sm leading-relaxed">Export your mix or stems and finish anywhere. From your mouth to master.</p>
              </div>
            </div>
          </div>

          {/* Closing CTA block */}
          <div className="mt-20 lg:mt-28 bb-card bg-[#0F1013] border-white/10 p-8 lg:p-14 text-center">
            <h2 className="font-display text-3xl md:text-4xl tracking-tighter">
              From your mouth to master.
            </h2>
            <p className="text-white/70 text-base mt-4 max-w-2xl mx-auto leading-relaxed">
              Create as many beats as your lips can handle. Beat Box is completely free while in beta&mdash;no
              gimmicks and no credit card required.
            </p>
            <p className="font-mono uppercase text-xs tracking-widest text-[#A8FF1F] mt-6">
              Your voice · Your ideas · Your music · You own what you create
            </p>
            <div className="mt-8 flex items-center justify-center gap-4 flex-wrap">
              <Link to="/register">
                <Button
                  data-testid="about-cta-register"
                  className="bb-pill bg-[#A8FF1F] hover:bg-[#94E600] text-[#0A0A0A] h-12 px-7 text-sm font-semibold"
                >
                  Start creating free <ArrowUpRight className="ml-2" size={16} weight="bold" />
                </Button>
              </Link>
              <Link to="/contact">
                <Button
                  data-testid="about-cta-contact"
                  variant="ghost"
                  className="bb-pill border-2 border-[#A8FF1F] hover:bg-[#A8FF1F]/10 text-white h-12 px-7 text-sm font-semibold"
                >
                  Contact us
                </Button>
              </Link>
            </div>
          </div>
        </section>
      </main>

      <footer className="border-t border-white/10">
        <div className="max-w-[1500px] mx-auto px-6 lg:px-12 py-8 flex flex-col md:flex-row items-start md:items-center justify-between gap-3">
          <div className="font-display text-xl tracking-tighter">BEAT BOX</div>
          <div className="text-white/40 text-xs font-mono uppercase tracking-widest">
            © 2026 Gruvy Graphics LLC
          </div>
        </div>
      </footer>
    </div>
  );
}
